import * as THREE from 'three';
import { byBody } from '../content/looks';
import { rideDef } from '../content/rides';
import type { Interactions } from '../systems/Interactions';
import type { ShopKind } from '../ui/ShopUI';
import { SPOTS } from '../world/layout';
import type { Activities } from './Activities';
import type { Driving } from './Driving';
import type { GameServices } from './services';
import type { StuntFlow } from './StuntFlow';

type Spot = { x: number; z: number };

/** Wires every "press E" spot in Lucky Acres to the activity it triggers. */
export function registerInteractions(
  s: GameServices,
  interactions: Interactions,
  deps: { activities: Activities; driving: Driving; stunts: StuntFlow },
): void {
  const { store, ui } = s;
  const { activities: act, driving } = deps;
  const add = interactions.add.bind(interactions);
  const at = (p: Spot) => () => p;
  const floatAtPlayer = (text: string, color: string): void =>
    ui.hud.float(text, s.player.headPosition(), color);

  // --- home (the front door and everything inside are in Interiors) ---------------
  add({
    id: 'couch',
    at: () => s.home().couch,
    radius: 1.7,
    prompt: () => (store.has('beer') ? 'Sit & crack a cold one' : 'Sit a spell'),
    action: () => act.sitOnCouch(),
  });
  add({
    id: 'grill',
    at: () => s.home().grill,
    radius: 1.5,
    prompt: () =>
      store.has('catfish')
        ? 'Fry up a catfish :catfish:'
        : store.has('hotdog')
          ? 'Grill a hot dog'
          : 'Grill (need a :hotdog: or :catfish:)',
    action: () => act.grill(s.home().grill, 45),
  });
  add({
    id: 'mailbox',
    at: () => s.home().mailbox,
    radius: 1.5,
    prompt: () =>
      store.doneToday('mail')
        ? null
        : store.minutes < 10 * 60
          ? 'Mailbox (mail comes at 10 AM)'
          : 'Check the mail',
    action: () => act.checkMail(),
  });
  add({
    id: 'camaro',
    at: () => ({ x: s.world.rideRoot.position.x, z: s.world.rideRoot.position.z }),
    radius: 3.2,
    prompt: () => {
      const ride = driving.currentRide();
      if (!ride || driving.active) return null;
      if (ride !== 'camaro') return `Drive the ${rideDef(ride).name}`;
      if (store.state.camaro >= 100) return 'Drive the Camaro';
      return `Work on the Camaro (${Math.round(store.state.camaro)}%)${store.has('parts') ? '' : ' (need :gear:)'}`;
    },
    action: () => (driving.drivable ? driving.start() : act.camaroAction()),
  });

  // --- around the park --------------------------------------------------------------
  add({
    id: 'tank',
    at: at(SPOTS.tankHouse),
    radius: 1.8,
    prompt: () => 'Pet Tank (very good boy)',
    action: () => {
      s.sfx('bark');
      ui.hud.float(':heart:', new THREE.Vector3(SPOTS.tankHouse.x, 1.2, SPOTS.tankHouse.z), '#ff6b8a');
      if (store.doneToday('tank')) return;
      store.markToday('tank');
      store.apply({ stats: { health: 3 }, clout: 1 });
      store.toast('Tank licks your entire face. Healing.', 'good', ':dog:');
    },
  });
  const shopAt = (id: string, spot: Spot, prompt: string, kind: ShopKind): void =>
    add({
      id,
      at: at(spot),
      radius: 2,
      prompt: () => prompt,
      action: () => ui.shop.open(kind),
      priority: 0.5,
    });
  shopAt('junk', SPOTS.junkyardWindow, 'Sell to Junkyard Jerry', 'junkyard');
  shopAt('jobs', SPOTS.jobBoard, 'Check the job board', 'jobs');
  shopAt('dave', SPOTS.bigDave, 'Talk double wides with Big Dave', 'bigdave');
  add({
    id: 'payphone',
    at: at(SPOTS.payphone),
    radius: 1.6,
    prompt: () => (store.doneToday('mama') ? null : 'Call Mama on the payphone (50¢)'),
    action: () => act.callMama(),
  });
  add({
    id: 'dumpster',
    at: at(SPOTS.dumpster),
    radius: 2.2,
    prompt: () => (store.doneToday('dumpster') ? null : 'Dumpster dive'),
    action: () => act.dumpsterDive(),
  });
  for (const agent of s.npcs.agents) {
    if (agent.def.id === 'jerry') continue; // Jerry's window is the junkyard shop
    add({
      id: `npc-${agent.def.id}`,
      at: () => agent.position,
      radius: 2.3,
      prompt: () => (agent.active && !agent.hold ? `Talk to ${agent.def.name}` : null),
      action: () => act.talk(agent),
      priority: 0.3,
      icon: ':speech:',
    });
  }
  add({
    id: 'job',
    at: () => s.jobs.interaction()?.at ?? { x: 9999, z: 9999 },
    radius: 2,
    prompt: () => s.jobs.interaction()?.prompt ?? null,
    action: () => s.jobs.interaction()?.action(),
    priority: 1,
  });

  // --- yard decor --------------------------------------------------------------------
  const slot = (id: string): Spot => s.home().decor[id] as Spot;
  const owned = (id: string, prompt: string | (() => string | null)) => (): string | null =>
    store.hasDecor(id) ? (typeof prompt === 'string' ? prompt : prompt()) : null;

  add({
    id: 'pool',
    at: () => ({ x: slot('pool').x, z: slot('pool').z + 2.9 }),
    radius: 1.6,
    prompt: owned('pool', 'Take a dip'),
    action: () => act.soak('pool'),
  });
  add({
    id: 'kiddie',
    at: () => slot('kiddiePool'),
    radius: 1.6,
    prompt: owned('kiddiePool', 'Soak your feet'),
    action: () => act.soak('kiddie'),
  });
  add({
    id: 'tub',
    at: () => ({ x: slot('tub').x, z: slot('tub').z + 0.9 }),
    radius: 1.5,
    prompt: owned('tub', 'Soak in the "hot tub"'),
    action: () => act.soak('tub'),
  });
  add({
    id: 'trampoline',
    at: () => ({ x: slot('trampoline').x, z: slot('trampoline').z + 2.2 }),
    radius: 1.6,
    prompt: owned('trampoline', 'Hold my beer (stunts)'),
    action: () => deps.stunts.menu(),
    priority: 0.5,
  });
  add({
    id: 'flag',
    at: () => slot('flag'),
    radius: 1.6,
    prompt: owned('flag', () => (store.doneToday('salute') ? null : 'Salute the flag')),
    action: () => {
      store.markToday('salute');
      store.addClout(1);
      floatAtPlayer(':us-flag: FREEDOM', '#ffffff');
      s.sfx('ding');
    },
  });
  add({
    id: 'goose',
    at: () => slot('goose'),
    radius: 1.4,
    prompt: owned('goose', "Change the goose's outfit"),
    action: () => {
      const n = Number(store.flag('goose_outfit') ?? 0) + 1;
      store.setFlag('goose_outfit', n);
      s.decor.setGooseOutfit(n + store.day);
      if (!store.doneToday('goose')) {
        store.markToday('goose');
        store.addClout(1);
      }
      floatAtPlayer(':porch-goose: Fashion!', '#ffd400');
    },
  });
  add({
    id: 'smoker',
    at: () => slot('smoker'),
    radius: 1.6,
    prompt: owned('smoker', () =>
      store.has('hotdog') ? 'Smoke a dog (+60 hunger)' : 'Smoker (need a :hotdog:)',
    ),
    action: () => act.grill(slot('smoker'), 60),
  });
  add({
    id: 'recliner',
    at: () => slot('recliner'),
    radius: 1.5,
    prompt: owned('recliner', () => `Recline like a ${byBody(store.state.player.look, 'king', 'queen')}`),
    action: () => act.recline(),
  });
  add({
    id: 'possum',
    at: () => slot('possum'),
    radius: 1.3,
    prompt: owned('possum', 'Pet Kevin'),
    action: () => {
      floatAtPlayer(':possum::heart:', '#ff9ad5');
      if (store.doneToday('kevin')) return;
      store.markToday('kevin');
      store.apply({ stats: { health: 3 } });
    },
  });
  add({
    id: 'gorilla',
    at: () => slot('gorilla'),
    radius: 2,
    prompt: owned('gorilla', () => (store.doneToday('gorilla') ? null : 'High-five the gorilla')),
    action: () => {
      store.markToday('gorilla');
      store.addClout(1);
      s.sfx('boing');
      floatAtPlayer(':high-five: UP TOP', '#c77dff');
    },
  });
}
